import isEmpty from 'lodash/isEmpty';
import noop from 'lodash/noop';
import pick from 'lodash/pick';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ESearchFilters } from '@/constants/searchFilters';
import useQueryParams from '@/hooks/useQueryParams';
import { applySearchFilter } from '@/redux/modules/search';
import type { IRenterSearchedInfo } from '@/services/analytics/search/types';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { EDeliveryOption } from '@/services/types/core/delivery.types';

import { EGuidedSearchComponentType, TGuidedSearchComponent } from '../guided-search/types';
import { useGuidedSearchComponents } from '../guided-search/useGuidedSearchComponents';
import { trackRenterSearched } from '../guided-search-2.0/analytics';

const singleSelectionComponents = [EGuidedSearchComponentType.ButtonGroupRadio];

type TGuidedSearchContext = {
  reset: () => void;
  goToPrevStep?: () => void;
  goToNextStep?: () => void;
  applySelection: () => void;
  currentStep: number;
  numberOfSteps: number;
  currentStepComponent?: TGuidedSearchComponent;
  currentStepSelection?: Record<string, string | string[] | undefined>;
  updateCurrentStepSelection: (fieldId: string, value?: string | string[]) => void;
  hasSelectedData?: boolean;
  hasTowSelected: boolean;
};

const LocalityGuidedSearchContext = createContext<TGuidedSearchContext>({
  reset: noop,
  applySelection: noop,
  currentStep: 1,
  numberOfSteps: 1,
  updateCurrentStepSelection: noop,
  hasTowSelected: false,
});

export const useLocalityGuidedSearchContext = () => useContext(LocalityGuidedSearchContext);

type TStepsSelection = Record<string, Record<string, string | string[] | undefined>>;

export const LocalityGuidedSearchProvider: React.FC<
  React.PropsWithChildren<{
    onApplySelection?: () => void;
    source?: IRenterSearchedInfo['source'];
    guidedSearchData?: any;
  }>
> = ({ onApplySelection, source = 'guided_search_1_locality', guidedSearchData, children }) => {
  const dispatch = useDispatch();
  const queryParams = useQueryParams();

  // Check if towable experiment is enabled
  const isTowableExperimentEnabled = useExperimentIsEnabled(OptimizelyFlags.TOWABLE_GUIDED_SEARCH);
  const { data: guidedSearchDataFromHook } = useGuidedSearchComponents(guidedSearchData);
  const finalGuidedSearchData = guidedSearchDataFromHook;

  // Filter out the components that contains non recognized component types
  const guidedSearchStepsData = useMemo(() => {
    return finalGuidedSearchData?.data_file.components.reduce((acc, component) => {
      const fields = component.fields.filter(field =>
        Object.values(EGuidedSearchComponentType).includes(field.component_type),
      );

      if (fields.length) {
        return [...acc, { ...component, fields }];
      }

      return acc;
    }, [] as TGuidedSearchComponent[]);
  }, [finalGuidedSearchData]);

  const numberOfSteps = guidedSearchStepsData?.length || 0;

  const [currentStep, setCurrentStep] = useState(1);
  const [stepsSelection, setStepsSelection] = useState<TStepsSelection>({});
  const [hasTowSelected, setHasTowSelected] = useState(false);

  useEffect(() => {
    if (window && window.sessionStorage) {
      const savedState = window.sessionStorage.getItem('guidedSearch1State');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        setStepsSelection(parsedState);
      }
    }
  }, []);

  const reset = () => {
    setCurrentStep(1);
    setHasTowSelected(false);
  };

  const goToPrevStep =
    currentStep > 1
      ? () => {
          const prevStep = currentStep - 1;
          setCurrentStep(prevStep);

          // If we're moving away from the last step (page-4), reset hasTowSelected
          if (currentStep === numberOfSteps) {
            setHasTowSelected(false);
          }
        }
      : undefined;

  const goToNextStep =
    currentStep < numberOfSteps
      ? () => {
          setCurrentStep(currentStep + 1);
        }
      : undefined;

  const applySelection = async (currentStepsSelection?: TStepsSelection) => {
    const selection = currentStepsSelection || stepsSelection;

    if (!guidedSearchStepsData || isEmpty(selection)) return;

    const params: Record<string, string> = {};

    // Build the query params based on the selected options
    guidedSearchStepsData.forEach(step => {
      if (!selection[step.id]) return;

      step.fields.forEach(field => {
        if (!selection[step.id]?.[field.id]) return;

        if (field.component_type === EGuidedSearchComponentType.ButtonGroupRadio) {
          const selectedOption = field.properties.rows.find(
            row => row.id === selection[step.id]?.[field.id],
          );

          if (selectedOption) {
            selectedOption.query_params.forEach(param => {
              params[param.key] = params[param.key]
                ? `${params[param.key]},${param.value}`
                : param.value;
            });
          }
        }
      });
    });

    if (params['delivery']) {
      params['deliveryStationary'] = EDeliveryOption.MOVING;
    }

    trackRenterSearched(params, {
      source,
    });

    dispatch(
      applySearchFilter(
        {
          // Persist location & date selection
          ...pick(queryParams, [
            ESearchFilters.ADDRESS,
            ESearchFilters.BOUNDS_NE,
            ESearchFilters.BOUNDS_SW,
            ESearchFilters.DATE_FROM,
            ESearchFilters.DATE_TO,
          ]),
          ...params,
          [ESearchFilters.SKIP_DEFAULTS]: 'true',
        },

        false,
        false,
        false,
        undefined,
        true,
      ),
    );

    onApplySelection?.();
  };

  const currentStepComponent = guidedSearchStepsData?.[currentStep - 1];

  // Update hasTowSelected based on current step and selection
  useEffect(() => {
    if (
      isTowableExperimentEnabled &&
      currentStep === numberOfSteps &&
      currentStepComponent?.id === 'page-4' &&
      stepsSelection['page-4']?.['tow-drive-delivery'] === '1'
    ) {
      setHasTowSelected(true);
    } else {
      setHasTowSelected(false);
    }
  }, [
    isTowableExperimentEnabled,
    currentStep,
    numberOfSteps,
    currentStepComponent,
    stepsSelection,
  ]);

  const moveToNextStepAfterSelection = useMemo(() => {
    if (!currentStepComponent) return false;

    return (
      currentStepComponent.fields.length === 1 &&
      currentStepComponent.fields.every(field =>
        singleSelectionComponents.includes(field.component_type),
      )
    );
  }, [currentStepComponent]);

  // Check if the selection is for "Tow" on page-4
  const checkIfTowSelected = (fieldId: string, value?: string | string[]) => {
    if (!isTowableExperimentEnabled) {
      return false;
    }

    if (currentStepComponent?.id !== 'page-4') {
      return false;
    }

    // Simple check: field is 'tow-drive-delivery' and value is '1' (Tow option)
    const isTow = fieldId === 'tow-drive-delivery' && value === '1';
    return isTow;
  };

  const currentStepSelection = stepsSelection[currentStepComponent?.id || ''];
  const updateCurrentStepSelection = (fieldId: string, value?: string | string[]) => {
    if (!currentStepComponent) return;

    const nextStepsSelection: TStepsSelection = {
      ...stepsSelection,
      [currentStepComponent.id]: {
        ...stepsSelection[currentStepComponent.id],
        [fieldId]: value,
      },
    };

    setStepsSelection(nextStepsSelection);

    if (window && window.sessionStorage) {
      window.sessionStorage.setItem('guidedSearch1State', JSON.stringify(nextStepsSelection));
    }

    // Check if Tow is selected
    const isTowSelected = checkIfTowSelected(fieldId, value);
    setHasTowSelected(isTowSelected);

    // Auto-advance logic: advance to next step UNLESS Tow is selected
    if (moveToNextStepAfterSelection && !isTowSelected) {
      (goToNextStep || applySelection)(nextStepsSelection);
    }
  };

  const hasSelectedData = !!(stepsSelection && Object.keys(stepsSelection).length);

  if (!guidedSearchStepsData?.length) return null;

  return (
    <LocalityGuidedSearchContext.Provider
      value={{
        reset,
        goToPrevStep,
        goToNextStep,
        applySelection,
        currentStep,
        numberOfSteps,
        currentStepComponent,
        currentStepSelection,
        updateCurrentStepSelection,
        hasSelectedData,
        hasTowSelected,
      }}>
      {children}
    </LocalityGuidedSearchContext.Provider>
  );
};
